import { alpha, NeutralColors } from "@mui/material/styles";

import { ACCENT, BRAND, NEUTRAL, PLUM, SIGNAL } from "./tokens";

import type { PaletteColor } from "@mui/material";

/**
 * MUI palette entries, built from the MACONIA 2026 tokens in `./tokens.ts`.
 * See docs/brand-implementation-guide.md.
 *
 * Two rules survive the translation into MUI's light/main/dark shape:
 *   - `main` is the *fill*. Where MUI writes text in `main` (buttons in the "text" and
 *     "outlined" variants, links), the accent's `main` would be 3.12:1 — so the accent
 *     palette's `dark` is `accent-strong` and the components override text to it.
 *   - `contrastText` on the accent is white, and only legal on a bold ≥16px label.
 */

const withAlphas = (color: PaletteColor): PaletteColor => {
	return {
		...color,
		alpha4: alpha(color.main, 0.04),
		alpha8: alpha(color.main, 0.08),
		alpha12: alpha(color.main, 0.12),
		alpha30: alpha(color.main, 0.3),
		alpha50: alpha(color.main, 0.5),
	};
};

/** The brand accent. `dark` is `accent-strong`, the only orange that may be text. */
export const accent = withAlphas({
	lightest: ACCENT.tint,
	light: ACCENT.tint,
	main: BRAND.accent,
	dark: ACCENT.strong,
	darkest: BRAND.ink,
	contrastText: BRAND.white,
});

/** Retained preset name — it now resolves to the brand accent, not to blue. */
export const browserbite: PaletteColor = {
	light: ACCENT.tint,
	main: BRAND.accent,
	dark: ACCENT.strong,
	contrastText: BRAND.white,
};

/** §2.3 — the warm ramp. Every step leans yellow, like `subtle` does. */
export const neutral: NeutralColors = {
	50: NEUTRAL[50],
	100: NEUTRAL[100],
	200: NEUTRAL[200],
	300: NEUTRAL[300],
	400: NEUTRAL[400],
	500: NEUTRAL[500],
	600: NEUTRAL[600],
	700: NEUTRAL[700],
	800: BRAND.text,
	900: BRAND.ink,
};

/** Amber, not orange — beside a #FF5A24 CTA an orange warning reads as a button. */
export const yellow = withAlphas({
	lightest: NEUTRAL[25],
	light: SIGNAL.warningLight,
	main: SIGNAL.warning,
	dark: SIGNAL.warningInk,
	darkest: SIGNAL.warningInk,
	contrastText: BRAND.ink, // put ink on amber, never white — 2.27:1
});

/** Kept for the `blue` colour preset; there is no blue in the brand, so it is ink. */
export const blue = withAlphas({
	lightest: NEUTRAL[25],
	light: NEUTRAL[200],
	main: BRAND.ink,
	dark: BRAND.ink,
	darkest: BRAND.ink,
	contrastText: BRAND.white,
});

export const green = withAlphas({
	lightest: NEUTRAL[25],
	light: SIGNAL.successLight,
	main: SIGNAL.success,
	dark: "#198754",
	darkest: "#226B0D",
	contrastText: BRAND.white,
});

export const indigo = withAlphas({
	lightest: NEUTRAL[25],
	light: NEUTRAL[200],
	main: PLUM,
	dark: PLUM,
	darkest: BRAND.ink,
	contrastText: BRAND.white,
});

export const purple = withAlphas({
	lightest: NEUTRAL[25],
	light: NEUTRAL[200],
	main: PLUM,
	dark: PLUM,
	darkest: BRAND.ink,
	contrastText: BRAND.white,
});

export const success = withAlphas({
	lightest: NEUTRAL[25],
	light: SIGNAL.successLight,
	main: SIGNAL.success,
	dark: "#198754",
	darkest: "#226B0D",
	contrastText: BRAND.white,
});

/** No "info" hue in the brand — informational chrome is a warm neutral. */
export const info = withAlphas({
	lightest: NEUTRAL[25],
	light: NEUTRAL[200],
	main: BRAND.subtle,
	dark: NEUTRAL[700],
	darkest: BRAND.ink,
	contrastText: BRAND.white,
});

export const warning = withAlphas({
	lightest: NEUTRAL[25],
	light: SIGNAL.warningLight,
	main: SIGNAL.warning,
	dark: SIGNAL.warningInk,
	darkest: SIGNAL.warningInk,
	contrastText: BRAND.ink,
});

export const error = withAlphas({
	lightest: NEUTRAL[25],
	light: SIGNAL.errorLight,
	main: SIGNAL.danger,
	dark: "#B02000",
	darkest: "#7A1700",
	contrastText: BRAND.white,
});
